import { Container } from 'pixi.js';
import { Scene } from '../app/SceneManager';
import { COLORS, DESIGN_W } from '../app/theme';
import { Panel, Button, makeText, sceneBackground, glassCard, iconSprite } from '../ui/widgets';
import { TextField } from '../ui/TextField';
import { ListModal } from '../ui/ListModal';
import { pageHeader, enterFade } from './common';
import type { AppContext } from '../app/AppContext';
import { MessageTag } from '../net/messageTags';
import {
  encodeCashRequest,
  decodeCash,
  encodePay,
  decodePayUrl,
  encodeRequestPayback,
  encodeCancelPayback,
  encodeAgentRequest,
  decodeAgentCode,
  encodeAgentListRequest,
  decodeAgentList,
  encodeRecheckPayments,
  encodeTransactionListRequest,
  decodeTransactionList,
  type CashState,
} from '../net/codec/finance';
import { moneyFa, toPersianDigits } from '../util/persian';
import { RoomName } from '../net/enums';
import { formatMoneyInput, parseMoneyInput } from '../util/moneyInput';
import { openExternalUrl } from '../platform/telegram';
import { loadLastDepositAmount, saveLastDepositAmount } from '../platform/storage';
import { gameState } from '../state/GameState';
import {
  DEPOSIT_AMOUNT_RULE,
  PAYBACK_AMOUNT_RULE,
  maximumWalletAmount,
  minimumWalletAmount,
  nextWalletAmount,
  previousWalletAmount,
  validateWalletAmount,
  type WalletAmountRule,
} from '../util/walletAmounts';

/**
 * Wallet / cash panel (port of CashPage.cs), re-skinned to the Login/Register glass-card style: a
 * balance hero card, a cash-in (buy) card, a cash-out (withdraw) card, a history/recheck utility row,
 * an agent-referral card, and the transaction history list with an empty state. The read path (Cash,
 * AgentRequest, AgentList, TransactionList) is fully wired; money ACTIONS stay user-initiated and the
 * codec is unchanged.
 */
export class PaymentScene extends Scene {
  private cash: CashState | null = null;
  private readonly body = new Container();
  private depositField?: TextField;
  private withdrawField?: TextField;
  private buyButton?: Button;
  private withdrawButton?: Button;
  private depositDecreaseButton?: Button;
  private lastDepositButton?: Button;
  private withdrawDecreaseButton?: Button;
  private maximumWithdrawButton?: Button;
  private lastDepositAmount: bigint | null = null;
  private pendingDepositAmount: bigint | null = null;
  private payPending = false;
  private paybackPending = false;
  private awaitingPaymentReturn = false;
  private pendingTransactionModal = false;
  private agentUsers: string[] = [];
  private agentUserCount = 0;
  private agentRequested = false;
  private agentRequestPending = false;
  private agentRequestButton?: Button;
  private pendingAgentModal = false;

  constructor(private readonly ctx: AppContext) {
    super();
  }

  onEnter(): void {
    const savedDeposit = loadLastDepositAmount(gameState.get().userId);
    this.lastDepositAmount = savedDeposit !== null && validateWalletAmount(savedDeposit, DEPOSIT_AMOUNT_RULE) === 'valid'
      ? savedDeposit
      : null;

    this.addChild(sceneBackground());
    const header = pageHeader('کیف پول', () => this.ctx.navigate(RoomName.MainMenu), this.ctx.iconTexture('wallet'));
    this.addChild(header, this.body);

    enterFade([header, this.body]);

    this.track(
      this.ctx.client.on(MessageTag.Cash, (r) => {
        this.paybackPending = false;
        this.cash = decodeCash(r);
        this.build();
      }),
    );
    this.track(
      this.ctx.client.on(MessageTag.Pay, (r) => {
        const url = decodePayUrl(r);
        const acceptedAmount = this.pendingDepositAmount;
        this.pendingDepositAmount = null;
        this.payPending = false;
        this.syncActionState();
        if (url) {
          if (acceptedAmount !== null) {
            this.lastDepositAmount = acceptedAmount;
            saveLastDepositAmount(gameState.get().userId, acceptedAmount);
            this.syncAmountShortcutState();
          }
          this.ctx.dialog({
            title: 'درگاه پرداخت',
            message: 'درخواست خرید سکه آماده است. برای ادامه وارد درگاه پرداخت شوید.',
            okText: 'ورود به درگاه پرداخت',
            onClose: () => {
              this.awaitingPaymentReturn = true;
              openExternalUrl(url);
            },
          });
        }
      }),
    );
    this.track(
      this.ctx.client.on(MessageTag.AgentRequest, (r) => {
        const agentCode = decodeAgentCode(r);
        this.agentRequestPending = false;
        if (!this.cash) return;
        this.cash = { ...this.cash, isAgent: true, agentCode };
        this.build();
      }),
    );
    // Server-side validation failures arrive through the global SendMessage dialog. Re-enable the
    // local action as well so the user can correct the value immediately.
    this.track(
      this.ctx.client.on(MessageTag.SendMessage, () => {
        if (!this.payPending && !this.paybackPending && !this.agentRequestPending) return;
        this.pendingDepositAmount = null;
        this.payPending = false;
        this.paybackPending = false;
        this.agentRequestPending = false;
        this.syncActionState();
      }),
    );
    this.track(
      this.ctx.client.on(MessageTag.TransactionList, (r) => {
        const transactions = decodeTransactionList(r);
        if (!this.pendingTransactionModal) return;
        this.pendingTransactionModal = false;
        this.openTransactionModal(transactions);
      }),
    );
    this.track(
      this.ctx.client.on(MessageTag.AgentList, (r) => {
        const a = decodeAgentList(r);
        this.agentUserCount = a.totalUserCount;
        this.agentUsers = a.users;
        if (this.pendingAgentModal) {
          this.pendingAgentModal = false;
          this.openAgentModal();
        } else {
          this.build(); // refresh the referred-user count pill
        }
      }),
    );
    const refreshAfterGateway = (): void => {
      if (!this.awaitingPaymentReturn || document.hidden) return;
      this.awaitingPaymentReturn = false;
      this.ctx.client.send(encodeCashRequest());
    };
    document.addEventListener('visibilitychange', refreshAfterGateway);
    window.addEventListener('focus', refreshAfterGateway);
    this.track(() => document.removeEventListener('visibilitychange', refreshAfterGateway));
    this.track(() => window.removeEventListener('focus', refreshAfterGateway));
    this.ctx.client.send(encodeCashRequest());
  }

  /** Re-flow on viewport-height change (Telegram fullscreen resolves after boot). Preserve typed edits. */
  onResize(): void {
    if (!this.cash) return;
    const dep = this.depositField?.value ?? '';
    const wd = this.withdrawField?.value ?? '';
    this.build();
    if (this.depositField) this.depositField.value = dep;
    if (this.withdrawField) this.withdrawField.value = wd;
  }

  private build(): void {
    const c = this.cash;
    if (!c) return;
    const depositValue = this.depositField?.value ?? '';
    const withdrawValue = this.withdrawField?.value ?? '';
    for (const child of this.body.removeChildren()) child.destroy({ children: true });
    this.depositField = undefined;
    this.withdrawField = undefined;
    this.buyButton = undefined;
    this.withdrawButton = undefined;
    this.depositDecreaseButton = undefined;
    this.lastDepositButton = undefined;
    this.withdrawDecreaseButton = undefined;
    this.maximumWithdrawButton = undefined;
    this.agentRequestButton = undefined;
    const w = DESIGN_W - 80;
    const left = 40;
    const pad = 24;
    const innerW = w - pad * 2;
    const fieldX = left + pad;
    const coinTex = this.ctx.iconTexture('coin');

    // Once we know the player is an agent, fetch their referred-user count for the hero pill.
    if (c.isAgent && !this.agentRequested) {
      this.agentRequested = true;
      this.ctx.client.send(encodeAgentListRequest());
    }

    // ── Balance hero card: balance rendered directly on the card (no nested pill frame). ──
    const heroY = 112;
    const heroH = c.isAgent ? 156 : 132;
    this.body.addChild(glassCard(left, heroY, w, heroH));
    const cap = makeText('موجودی قابل استفاده', { size: 25, fill: COLORS.textDim, align: 'center' });
    cap.anchor.set(0.5, 0);
    cap.position.set(DESIGN_W / 2, heroY + 16);
    this.body.addChild(cap);
    const amount = makeText(moneyFa(c.balance), { size: 40, weight: '700', fill: COLORS.gold, align: 'center' });
    amount.anchor.set(0.5, 0);
    const coinSize = 42;
    const coinGap = 10;
    const amountY = heroY + 54;
    amount.position.set(DESIGN_W / 2 - (coinGap + coinSize) / 2, amountY);
    this.body.addChild(amount);
    const coin = iconSprite(coinTex, coinSize);
    coin.position.set(amount.x + amount.width / 2 + coinGap, amountY + amount.height / 2 - coinSize / 2);
    this.body.addChild(coin);
    if (c.isAgent) {
      const countY = heroY + heroH - 36;
      const ct = makeText(`کاربران معرفی‌شده: ${toPersianDigits(this.agentUserCount)}`, { size: 23, fill: COLORS.textDim, align: 'center' });
      ct.anchor.set(0.5, 0);
      const uSize = 22;
      const uGap = 8;
      ct.position.set(DESIGN_W / 2 - (uGap + uSize) / 2, countY);
      this.body.addChild(ct);
      const uIcon = iconSprite(this.ctx.iconTexture('users'), uSize);
      uIcon.alpha = 0.85;
      uIcon.position.set(ct.x + ct.width / 2 + uGap, countY - 1);
      this.body.addChild(uIcon);
    }

    // ── Cash-in card: deposit amount (top) + the primary gold «خرید سکه» (below). ──
    const ciY = heroY + heroH + 16;
    const ciH = 234;
    this.body.addChild(glassCard(left, ciY, w, ciH));
    this.depositField = new TextField({
      width: innerW,
      height: 68,
      value: depositValue,
      placeholder: 'مبلغ شارژ (تومان)',
      align: 'center',
      icon: coinTex ?? null,
      clearable: true,
      clearAccessibilityLabel: 'پاک کردن مبلغ شارژ',
      accessibilityLabel: 'مبلغ شارژ به تومان',
      inputMode: 'numeric',
      enterKeyHint: 'done',
    });
    this.depositField.position.set(fieldX, ciY + 14);
    this.body.addChild(this.depositField);
    this.bindMoneyField(this.depositField);
    this.depositField.onKeyboardEnter(() => this.pay());

    // General shortcuts fill the editable field for review. The primary action remains explicit, so
    // repeated stepping or recalling a previous amount can never start a payment accidentally.
    const depositStepLabel = this.shortAmountLabel(DEPOSIT_AMOUNT_RULE.step);
    const shortcutGap = 10;
    const shortcutCount = 3;
    const shortcutW = (innerW - shortcutGap * (shortcutCount - 1)) / shortcutCount;
    const depositShortcuts = [
      new Button(`+${depositStepLabel}`, () => {
        this.setMoneyAmount(this.depositField, nextWalletAmount(this.parseAmount(this.depositField), DEPOSIT_AMOUNT_RULE));
      }, {
        width: shortcutW,
        height: 44,
        fontSize: 18,
        fill: COLORS.panelSoft,
        textColor: COLORS.text,
        accessibleLabel: `افزایش مبلغ شارژ به اندازه ${depositStepLabel}`,
      }),
      new Button('مبلغ قبلی', () => {
        if (this.lastDepositAmount !== null) this.setMoneyAmount(this.depositField, this.lastDepositAmount);
      }, {
        width: shortcutW,
        height: 44,
        fontSize: 18,
        fill: COLORS.panelSoft,
        textColor: COLORS.text,
        accessibleLabel: 'استفاده از مبلغ شارژ قبلی',
      }),
      new Button(`−${depositStepLabel}`, () => {
        this.setMoneyAmount(this.depositField, previousWalletAmount(this.parseAmount(this.depositField), DEPOSIT_AMOUNT_RULE));
      }, {
        width: shortcutW,
        height: 44,
        fontSize: 18,
        fill: COLORS.panelSoft,
        textColor: COLORS.text,
        accessibleLabel: `کاهش مبلغ شارژ به اندازه ${depositStepLabel}`,
      }),
    ];
    this.lastDepositButton = depositShortcuts[1];
    this.depositDecreaseButton = depositShortcuts[2];
    depositShortcuts.forEach((shortcut, index) => {
      shortcut.position.set(fieldX + (depositShortcuts.length - 1 - index) * (shortcutW + shortcutGap), ciY + 90);
      this.body.addChild(shortcut);
    });

    this.buyButton = new Button('خرید سکه', () => this.pay(), {
      width: innerW,
      height: 68,
      fontSize: 26,
      icon: coinTex ?? null,
      accessibleLabel: 'خرید سکه',
    });
    this.buyButton.position.set(fieldX, ciY + 148);
    this.body.addChild(this.buyButton);

    // ── Cash-out card: pending status + cancel, or withdraw amount + request. ──
    const coY = ciY + ciH + 16;
    if (c.hasPayback) {
      const coH = 178;
      this.body.addChild(glassCard(left, coY, w, coH));
      const row = this.iconLine(`در انتظار برداشت: ${moneyFa(c.paybackAmount)}`, this.ctx.iconTexture('clock'), coY + 22);
      this.body.addChild(row);
      const cancel = new Button('لغو درخواست برداشت', () => this.ctx.client.send(encodeCancelPayback()), {
        width: innerW,
        height: 76,
        fontSize: 26,
        fill: COLORS.danger,
      });
      cancel.position.set(fieldX, coY + coH - 16 - 76);
      this.body.addChild(cancel);
    } else {
      const coH = 264;
      this.body.addChild(glassCard(left, coY, w, coH));
      this.withdrawField = new TextField({
        width: innerW,
        height: 80,
        value: withdrawValue,
        placeholder: 'مبلغ',
        align: 'center',
        icon: this.ctx.iconTexture('wallet') ?? null,
        clearable: true,
        clearAccessibilityLabel: 'پاک کردن مبلغ برداشت',
        accessibilityLabel: 'مبلغ برداشت',
        inputMode: 'numeric',
        enterKeyHint: 'done',
      });
      this.withdrawField.position.set(fieldX, coY + 18);
      this.body.addChild(this.withdrawField);
      this.bindMoneyField(this.withdrawField);
      this.withdrawField.onKeyboardEnter(() => this.requestPayback());

      const paybackStepLabel = this.shortAmountLabel(PAYBACK_AMOUNT_RULE.step);
      const paybackShortcuts = [
        new Button(`+${paybackStepLabel}`, () => {
          this.setMoneyAmount(this.withdrawField, nextWalletAmount(this.parseAmount(this.withdrawField), PAYBACK_AMOUNT_RULE));
        }, {
          width: shortcutW,
          height: 44,
          fontSize: 18,
          fill: COLORS.panelSoft,
          textColor: COLORS.text,
          accessibleLabel: `افزایش مبلغ برداشت به اندازه ${paybackStepLabel}`,
        }),
        new Button('حداکثر موجودی', () => {
          this.setMoneyAmount(this.withdrawField, maximumWalletAmount(c.balance, PAYBACK_AMOUNT_RULE));
        }, {
          width: shortcutW,
          height: 44,
          fontSize: 18,
          fill: COLORS.panelSoft,
          textColor: COLORS.text,
          accessibleLabel: 'تنظیم مبلغ برداشت روی حداکثر موجودی',
        }),
        new Button(`−${paybackStepLabel}`, () => {
          this.setMoneyAmount(this.withdrawField, previousWalletAmount(this.parseAmount(this.withdrawField), PAYBACK_AMOUNT_RULE));
        }, {
          width: shortcutW,
          height: 44,
          fontSize: 18,
          fill: COLORS.panelSoft,
          textColor: COLORS.text,
          accessibleLabel: `کاهش مبلغ برداشت به اندازه ${paybackStepLabel}`,
        }),
      ];
      this.maximumWithdrawButton = paybackShortcuts[1];
      this.withdrawDecreaseButton = paybackShortcuts[2];
      paybackShortcuts.forEach((shortcut, index) => {
        shortcut.position.set(fieldX + (paybackShortcuts.length - 1 - index) * (shortcutW + shortcutGap), coY + 106);
        this.body.addChild(shortcut);
      });

      this.withdrawButton = new Button('درخواست برداشت', () => this.requestPayback(), {
        width: innerW,
        height: 80,
        fontSize: 27,
        fill: COLORS.panel,
        textColor: COLORS.text,
      });
      this.withdrawButton.position.set(fieldX, coY + 166);
      this.body.addChild(this.withdrawButton);
    }
    const coBottom = coY + (c.hasPayback ? 178 : 264);

    // ── Utility row: history + recheck failed purchases (secondary). ──
    const uY = coBottom + 16;
    const half = (w - 16) / 2;
    const txBtn = new Button('تاریخچه', () => {
      this.pendingTransactionModal = true;
      this.ctx.client.send(encodeTransactionListRequest());
    }, {
      width: half,
      height: 72,
      fontSize: 24,
      fill: COLORS.panelSoft,
      textColor: COLORS.text,
      icon: this.ctx.iconTexture('clock'),
    });
    txBtn.position.set(left + half + 16, uY);
    this.body.addChild(txBtn);
    const recheck = new Button('بررسی خریدهای ناموفق', () => this.ctx.client.send(encodeRecheckPayments()), {
      width: half,
      height: 72,
      fontSize: 22,
      fill: COLORS.panelSoft,
      textColor: COLORS.text,
      icon: this.ctx.iconTexture('refresh'),
    });
    recheck.position.set(left, uY);
    this.body.addChild(recheck);

    // ── Agent referral: a card with code + list (agents), or a single "become agent" button. ──
    const y = uY + 72 + 16;
    if (c.isAgent) {
      const agH = 220;
      this.body.addChild(glassCard(left, y, w, agH));
      const code = makeText(`کد معرفی شما: ${c.agentCode}`, { size: 24, weight: '700', align: 'center', fill: COLORS.gold });
      code.anchor.set(0.5, 0);
      code.position.set(DESIGN_W / 2, y + 18);
      this.body.addChild(code);
      const note = makeText('کاربرانی که با این کد ثبت‌نام کنند در لیست کاربران شما قرار می‌گیرند.', {
        size: 22,
        fill: COLORS.textDim,
        align: 'center',
      });
      note.anchor.set(0.5, 0);
      note.style.wordWrap = true;
      note.style.wordWrapWidth = innerW;
      note.position.set(DESIGN_W / 2, y + 56);
      this.body.addChild(note);
      const listBtn = new Button('لیست کاربران معرفی شده', () => {
        this.pendingAgentModal = true;
        this.ctx.client.send(encodeAgentListRequest());
      }, { width: innerW, height: 64, fontSize: 24, fill: COLORS.panelSoft, textColor: COLORS.text, icon: this.ctx.iconTexture('users') });
      listBtn.position.set(fieldX, y + agH - 16 - 64);
      this.body.addChild(listBtn);
    } else {
      this.agentRequestButton = new Button('دریافت کد معرف', () => this.requestAgent(), {
        width: w,
        height: 72,
        fontSize: 25,
        fill: COLORS.panelSoft,
        textColor: COLORS.text,
        icon: this.ctx.iconTexture('users'),
      });
      this.agentRequestButton.position.set(left, y);
      this.body.addChild(this.agentRequestButton);
    }
    this.syncActionState();
    this.syncAmountShortcutState();
  }

  /** A centered icon + text line (RTL: text then icon to its right), anchored at top `y`. */
  private iconLine(text: string, icon: ReturnType<AppContext['iconTexture']>, y: number): Container {
    const c = new Container();
    const t = makeText(text, { size: 20, fill: COLORS.gold, align: 'center' });
    t.anchor.set(0, 0);
    const size = 26;
    const gap = 8;
    const totalW = t.width + gap + size;
    t.position.set(DESIGN_W / 2 - totalW / 2, y);
    c.addChild(t);
    const node = iconSprite(icon, size);
    node.position.set(t.x + t.width + gap, y - 2);
    c.addChild(node);
    return c;
  }

  /** The agent's referred-user list as a full-screen sub-page with a «بازگشت» button (Unity-style). */
  private openAgentModal(): void {
    const rows = this.agentUsers.map((u) => this.txRow(u));
    const modal = new ListModal('کاربران معرفی شده', rows, () => modal.destroy({ children: true }));
    this.addChild(modal);
  }

  /** Transaction and payback history as the same full-screen list used for referred users. */
  private openTransactionModal(transactions: string[]): void {
    const rows = transactions.map((transaction) => this.txRow(transaction));
    const modal = new ListModal('تاریخچه تراکنش‌ها', rows, () => modal.destroy({ children: true }));
    this.addChild(modal);
  }

  private txRow(text: string): Container {
    const c = new Container();
    const w = DESIGN_W - 80 - 30;
    c.addChild(new Panel(w, 88, { fill: COLORS.panelSoft }));
    const t = makeText(text, { size: 24 });
    t.anchor.set(1, 0.5);
    t.style.wordWrap = true;
    t.style.wordWrapWidth = w - 32;
    t.position.set(w - 16, 44);
    c.addChild(t);
    return c;
  }

  private bindMoneyField(field: TextField): void {
    field.onChange((text) => {
      const formatted = formatMoneyInput(text);
      if (field.value !== formatted) field.value = formatted;
      this.syncAmountShortcutState();
    });
  }

  private setMoneyAmount(field: TextField | undefined, amount: bigint): void {
    if (!field || amount <= 0n) return;
    field.value = formatMoneyInput(amount.toString());
    this.syncAmountShortcutState();
  }

  private shortAmountLabel(amount: bigint): string {
    if (amount >= 1_000n && amount % 1_000n === 0n) {
      return `${formatMoneyInput((amount / 1_000n).toString())} هزار`;
    }
    return formatMoneyInput(amount.toString());
  }

  private parseAmount(field?: TextField): bigint {
    return parseMoneyInput(field?.value ?? '');
  }

  private syncActionState(): void {
    this.buyButton?.setText(this.payPending ? 'در حال آماده‌سازی درگاه…' : 'خرید سکه').setEnabled(!this.payPending);
    this.withdrawButton?.setText(this.paybackPending ? 'در حال ثبت درخواست…' : 'درخواست برداشت').setEnabled(!this.paybackPending);
    this.agentRequestButton
      ?.setText(this.agentRequestPending ? 'در حال دریافت کد…' : 'دریافت کد معرف')
      .setEnabled(!this.agentRequestPending);
  }

  private syncAmountShortcutState(): void {
    const depositAmount = this.parseAmount(this.depositField);
    this.depositDecreaseButton?.setEnabled(depositAmount > minimumWalletAmount(DEPOSIT_AMOUNT_RULE));
    this.lastDepositButton?.setEnabled(this.lastDepositAmount !== null);

    const paybackAmount = this.parseAmount(this.withdrawField);
    this.withdrawDecreaseButton?.setEnabled(paybackAmount > minimumWalletAmount(PAYBACK_AMOUNT_RULE));
    this.maximumWithdrawButton?.setEnabled(
      this.cash !== null && maximumWalletAmount(this.cash.balance, PAYBACK_AMOUNT_RULE) > 0n,
    );
  }

  private validateAmount(amount: bigint, rule: Readonly<WalletAmountRule>, action: string): boolean {
    const validation = validateWalletAmount(amount, rule);
    if (validation === 'valid') return true;
    if (validation === 'required') {
      this.ctx.dialog({ message: `لطفاً مبلغ ${action} را وارد کنید.` });
    } else if (validation === 'below-minimum') {
      this.ctx.dialog({ message: `حداقل مبلغ ${action} ${formatMoneyInput(rule.min.toString())} است.` });
    } else {
      this.ctx.dialog({ message: `مبلغ ${action} باید مضرب ${formatMoneyInput(rule.step.toString())} باشد.` });
    }
    return false;
  }

  private pay(): void {
    if (this.payPending) return;
    const amount = this.parseAmount(this.depositField);
    if (!this.validateAmount(amount, DEPOSIT_AMOUNT_RULE, 'خرید سکه')) return;
    this.pendingDepositAmount = amount;
    this.payPending = true;
    this.syncActionState();
    this.ctx.client.send(encodePay(amount));
  }

  private requestPayback(): void {
    if (this.paybackPending) return;
    const amount = this.parseAmount(this.withdrawField);
    if (!this.validateAmount(amount, PAYBACK_AMOUNT_RULE, 'برداشت')) return;
    if (this.cash !== null && amount > this.cash.balance) {
      this.ctx.dialog({ message: 'موجودی کافی نیست.' });
      return;
    }
    this.paybackPending = true;
    this.syncActionState();
    this.ctx.client.send(encodeRequestPayback(amount));
  }

  private requestAgent(): void {
    if (this.agentRequestPending) return;
    this.agentRequestPending = true;
    this.syncActionState();
    this.ctx.client.send(encodeAgentRequest());
  }
}
